import { logger } from '../logging/logger'

/**
 * A clipboard write the host can inject via `FunkitProvider`'s `copyToClipboard`
 * to use their own clipboard module — e.g. `expo-clipboard` in Expo Go, where
 * the bare-RN native module isn't linked. Given the address text; returns
 * whether the write succeeded (a `void` return is treated as success).
 */
export type ClipboardWriter = (value: string) => void | Promise<boolean>

/** Normalize host writers: void succeeds; false or rejection fails without throwing. */
export async function writeClipboard(
  write: ClipboardWriter,
  value: string,
): Promise<boolean> {
  try {
    return (await write(value)) !== false
  } catch (error) {
    logger.warn('connect-rn: clipboard write threw; value not copied', {
      error: error instanceof Error ? error.message : String(error),
    })
    return false
  }
}

/**
 * Default clipboard: lazily (dynamic import) loads the bare-RN
 * `@react-native-clipboard/clipboard` peer and writes via `setString`.
 *
 * The import is dynamic + guarded so the native module is only **evaluated when
 * a copy actually happens** — not at startup. A host that hasn't linked it
 * (e.g. Expo Go, where `getEnforcing('RNCClipboard')` would throw) should inject
 * its own writer through `FunkitProvider`'s `copyToClipboard`; that override
 * runs instead, so this path — and the throw — never execute. If the module is
 * absent and no override is provided, the copy degrades to a no-op + warning
 * instead of crashing.
 *
 * Returns whether the copy succeeded.
 */
export async function copyToClipboard(value: string): Promise<boolean> {
  try {
    const { default: Clipboard } =
      await import('@react-native-clipboard/clipboard')
    Clipboard.setString(value)
    return true
  } catch (error) {
    logger.warn(
      'connect-rn: clipboard write failed; address not copied. Inject ' +
        'FunkitProvider `copyToClipboard` to use your own clipboard module ' +
        '(e.g. expo-clipboard in Expo Go).',
      { error: error instanceof Error ? error.message : String(error) },
    )
    return false
  }
}

/**
 * A clipboard read the host can inject via `FunkitProvider`'s
 * `pasteFromClipboard`, for the same reason as {@link ClipboardWriter}: the
 * bare-RN native module isn't linked everywhere. Returns the clipboard's text,
 * or `undefined` when it can't be read.
 */
export type ClipboardReader = () =>
  | string
  | undefined
  | Promise<string | undefined>

/** A {@link ClipboardReader} that resolves to `undefined` instead of rejecting. */
export type SafeClipboardReader = () => Promise<string | undefined>

/**
 * Wraps a host-injected reader where it enters the SDK, so no paste call site
 * has to defend itself. Only injected readers can fail; the default below is
 * already guarded, and the type lets a host throw as easily as reject.
 */
export function guardClipboardReader(
  read: ClipboardReader,
): SafeClipboardReader {
  return async () => {
    try {
      const value = await read()
      // A host that ignores the type can resolve a non-string (e.g. the
      // `ClipboardItem[]` of `navigator.clipboard.read`), and call sites go
      // straight to `.trim()` — same unhandled rejection this guard exists for.
      if (value !== undefined && typeof value !== 'string') {
        logger.warn('connect-rn:host_callback_threw', {
          callback: 'pasteFromClipboard',
          error: `resolved ${typeof value}, expected string`,
        })
        return undefined
      }
      return value
    } catch (error) {
      logger.warn('connect-rn:host_callback_threw', {
        callback: 'pasteFromClipboard',
        error: error instanceof Error ? error.message : String(error),
      })
      return undefined
    }
  }
}

/**
 * Default clipboard read — the mirror of {@link copyToClipboard}, and dynamic for
 * the same reason: the native module is only evaluated when a paste actually
 * happens, so a host that hasn't linked it degrades to a warning instead of
 * crashing at startup.
 */
export async function pasteFromClipboard(): Promise<string | undefined> {
  try {
    const { default: Clipboard } =
      await import('@react-native-clipboard/clipboard')
    return await Clipboard.getString()
  } catch (error) {
    logger.warn(
      'connect-rn: clipboard read failed; nothing pasted. Inject ' +
        'FunkitProvider `pasteFromClipboard` to use your own clipboard module ' +
        '(e.g. expo-clipboard in Expo Go).',
      { error: error instanceof Error ? error.message : String(error) },
    )
    return undefined
  }
}
